import type { KbAnswerResponse } from "../kb/types";
import type { KbLane } from "../kb/types";
import { kbAnswer } from "../kb/client";
import { fetchContactsPathAware, sortAndLimitContacts } from "../contacts/router";
import type { AskContext } from "./trainingAskTypes";
import type { TrainingAnswer } from "./types";
import {
  strictContactSelection,
  inferPreferredPrimaryTags,
  kbFetchFailedResponse,
} from "./trainingAskContactHelpers";

export type KbPhaseState = {
  out: KbAnswerResponse;
  mapped: TrainingAnswer;
  needsClarification: boolean;
  contacts: any[];
  contactsWhy: "policy_support" | "direct" | "";
  contactsError: string | null;
  answerMode: "kb" | "contacts" | "location";
};

/** Forces the contacts finalize path to load directory rows before any KB narrative (direct “who to contact” in Contacts lane). */
export function syntheticNoAnswerKbPhase(ctx: {
  guidancePurpose?: "contacts" | "policy" | "advice" | "pricing";
  guidanceProduct?: "all" | "sheds" | "steel";
  guidanceSource?: "user_selected" | "auto_rules" | "default";
}): KbPhaseState {
  return {
    out: {
      mode: "no_answer",
      answer: "",
      bestConfidence: 0,
      confidenceBand: "low",
      sources: [],
      guidancePurpose: ctx.guidancePurpose ?? "policy",
      guidanceProduct: ctx.guidanceProduct ?? "all",
      guidanceSource: ctx.guidanceSource ?? "default",
    },
    mapped: { answer: "", hits: [] },
    needsClarification: true,
    contacts: [],
    contactsWhy: "",
    contactsError: null,
    answerMode: "kb",
  };
}

export async function runTrainingKbPhase(params: {
  q: string;
  contextualQuery: string;
  ctx: AskContext;
  requestedLane: KbLane;
  topK: number;
  contactLimit: number;
  contactFetchLimit: number;
}): Promise<KbPhaseState> {
  const { q, contextualQuery, ctx, requestedLane, topK, contactLimit, contactFetchLimit } = params;
  console.log("[training/ask][debug] KB phase start", {
    requestedLane,
    queryPreview: (q || "").slice(0, 120),
    contextualQueryPreview: (contextualQuery || "").slice(0, 140),
    organizationId: ctx.organizationId || null,
    kbLocationId: ctx.kbLocationId || null,
  });

  let mapped: TrainingAnswer = { answer: "", hits: [] };
  let needsClarification = false;
  let contacts: any[] = [];
  let contactsWhy: "policy_support" | "direct" | "" = "";
  let contactsError: string | null = null;
  let answerMode: "kb" | "contacts" | "location" = "kb";

  const guidancePurpose = ctx.guidancePurpose ?? "policy";
  const guidanceProduct = ctx.guidanceProduct ?? "all";
  const guidanceSource = ctx.guidanceSource ?? "default";

  const kbQuery = ctx.queryKind === "followup" ? contextualQuery : q;

  let out: KbAnswerResponse;
  try {
    out = await kbAnswer({
      query: kbQuery,
      audience: "internal",
      locationId: ctx.kbLocationId,
      organizationId: ctx.organizationId ?? null,
      topK,
      guidancePurpose,
      guidanceProduct,
      guidanceSource,
      lane: requestedLane,
      laneOverride: null,
    });
  } catch (e) {
    out = kbFetchFailedResponse(e, requestedLane);
  }
  const pricingDiagnostics =
    out.pricingMode || (Array.isArray(out.pricingFacts) && out.pricingFacts.length > 0)
      ? {
          sourceModels: Array.from(
            new Set(
              (out.pricingFacts || [])
                .map((f) => (typeof f.pricingSourceModel === "string" ? f.pricingSourceModel.trim() : ""))
                .filter(Boolean)
            )
          ),
          pricingFactsCount: Array.isArray(out.pricingFacts) ? out.pricingFacts.length : 0,
          hasActivePricingFacts: Boolean(out.hasActivePricingFacts),
          skipReason:
            out.pricingDebug && typeof out.pricingDebug === "object" && "skipReason" in out.pricingDebug
              ? (out.pricingDebug as { skipReason?: string | null }).skipReason ?? null
              : null,
        }
      : undefined;

  if (out.mode === "no_answer") {
    console.log("[training/ask][debug] KB phase no_answer", {
      requestedLane,
      answerPreview: (out.answer || "").slice(0, 180),
      guidancePurpose: out.guidancePurpose || null,
      sourceCount: out.sources?.length ?? 0,
    });
    if (out.debug?.kbFailureCode) {
      console.warn("[training/ask] kb no_answer diagnostic", {
        kbFailureCode: out.debug.kbFailureCode,
        kbFailureStatus: out.debug.kbFailureStatus ?? null,
        kbRid: out.debug.kbRid ?? null,
      });
    }
    needsClarification = true;
    mapped = { answer: out.answer, hits: [], pricingDiagnostics };

    if (requestedLane === "contacts") {
      const trustOverride =
        ctx.trustLevelOverride === 1 || ctx.trustLevelOverride === 2 || ctx.trustLevelOverride === 3
          ? ctx.trustLevelOverride
          : null;
      const fallbackPeople = await fetchContactsPathAware({
        q,
        limit: contactFetchLimit,
        organizationId: ctx.organizationId ?? null,
        locationId: ctx.kbLocationId ?? null,
        trustLevelOverride: trustOverride,
      });
      console.log("[training/ask][debug] contacts fallback fetch from KB phase", {
        ok: fallbackPeople.ok,
        resultCount: fallbackPeople.ok ? fallbackPeople.results.length : 0,
        error: fallbackPeople.ok ? null : fallbackPeople.error,
      });
      if (fallbackPeople.ok) {
        const fallbackPreferredTags = inferPreferredPrimaryTags(q);
        const strict = strictContactSelection({
          rows: fallbackPeople.results,
          requiredTags: fallbackPreferredTags,
          limit: contactFetchLimit,
          fallback: fallbackPeople.generalFallback,
        });
        console.log("[training/ask][debug] contacts fallback strict selection from KB phase", {
          fallbackPreferredTags,
          strictSelectedCount: strict.selected.length,
          exactMatchCount: strict.exactMatchCount,
          relatedTagSuggestions: strict.relatedTagSuggestions,
        });
        if (strict.selected.length) {
          contacts = sortAndLimitContacts(strict.selected, contactLimit, fallbackPreferredTags);
          contactsWhy = "policy_support";
          contactsError = null;
          answerMode = "contacts";
        } else {
          contacts = [];
          contactsWhy = "policy_support";
          contactsError = "no contacts matched the current strict tag policy";
        }
      } else {
        contactsError = fallbackPeople.error;
      }
    }
  } else {
    needsClarification = false;
    const hitsSorted = (out.sources || [])
      .map((s: any) => ({
        id: s.slug,
        title: s.title,
        url: s.sourceUrl || undefined,
        score: s.score,
        docType: s.docType ?? null,
        freshnessState: s.freshnessState ?? "unknown",
        pricingGrounded: Boolean(s.pricingGrounded),
      }))
      .sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0));

    const seenIds = new Set<string>();
    const hits = hitsSorted.filter((h: { id: string }) => {
      if (seenIds.has(h.id)) return false;
      seenIds.add(h.id);
      return true;
    });

    mapped = { answer: out.answer, hits, pricingDiagnostics };
  }

  console.log("[training/ask][debug] KB phase end", {
    requestedLane,
    outMode: out.mode,
    mappedHits: mapped.hits?.length ?? 0,
    pricingMode: Boolean(out.pricingMode),
    pricingFactsCount: Array.isArray(out.pricingFacts) ? out.pricingFacts.length : 0,
    pricingSourceModels: pricingDiagnostics?.sourceModels ?? [],
    hasActivePricingFacts: pricingDiagnostics?.hasActivePricingFacts ?? false,
    pricingDebug: out.pricingDebug ?? null,
    pricingSkipReason:
      out.pricingDebug && typeof out.pricingDebug === "object" && "skipReason" in out.pricingDebug
        ? (out.pricingDebug as { skipReason?: string | null }).skipReason ?? null
        : null,
    contactsSeeded: contacts.length,
    contactsError,
    answerMode,
  });

  return { out, mapped, needsClarification, contacts, contactsWhy, contactsError, answerMode };
}
